// Order status and delivery status are separate (de-order-service, 2026-11-04): orderStatus ends at AUTHORIZED /
// REJECTED / CANCELED and `deliveryStatus` (ORDER_DELIVERY_STATUS) says where the goods are. A backend without that
// change sends no `deliveryStatus`, and rows written before migration 2026-11-04-01 still carry PREPARING /
// DELIVERING / DELIVERED as orderStatus. The order API maps both onto the new shape here, so every screen shows the
// two statuses apart either way. A `deliveryStatus` the backend sent is kept as is.

/** Delivery stages an older backend stored as the order status. */
export const LEGACY_DELIVERY_ORDER_STATUSES: readonly string[] = ["PREPARING", "DELIVERING", "DELIVERED"]

type DeliveryAware = {
  orderStatus?: string | null
  deliveryStatus?: string | null
  deliveredDate?: string | Date | null
  deliveryNote?: { deliveryNoteStatus?: string | null } | null
}

/**
 * The backend rule (utils/order-delivery-status.ts) from what an older response carries: the order's own delivery
 * note, its delivered date and its legacy status. Consolidated notes are not in the response, so the legacy status
 * fills in for them.
 */
export function legacyDeliveryStatus(order: DeliveryAware): string | null {
  const status = order.orderStatus ?? ""
  const dnStatus = order.deliveryNote?.deliveryNoteStatus
  if (dnStatus) return dnStatus === "DRAFT" ? "CREATED" : dnStatus
  if (status === "DELIVERED" || order.deliveredDate) return "DELIVERED"
  if (status === "DELIVERING") return "DELIVERING"
  return ["APPROVED", "AUTHORIZED", "PREPARING"].includes(status) ? "NOT_CREATED" : null
}

/** One order in the new shape: orderStatus without a delivery stage, `deliveryStatus` set. */
export function withSeparateDeliveryStatus<T>(order: T): T {
  if (!order || typeof order !== "object") return order
  const o = order as T & DeliveryAware
  const hasDeliveryStatus = "deliveryStatus" in (order as object)
  const legacyStatus = LEGACY_DELIVERY_ORDER_STATUSES.includes(o.orderStatus ?? "")
  if (hasDeliveryStatus && !legacyStatus) return order
  return {
    ...o,
    orderStatus   : legacyStatus ? "AUTHORIZED" : o.orderStatus,
    deliveryStatus: hasDeliveryStatus ? o.deliveryStatus : legacyDeliveryStatus(o),
  }
}

/** A list response ({ rows, … }) in the new shape. */
export function withSeparateDeliveryStatusRows<T>(res: T): T {
  const r = res as T & { rows?: unknown }
  if (!r || !Array.isArray(r.rows)) return res
  return { ...r, rows: r.rows.map((row) => withSeparateDeliveryStatus(row)) }
}
